import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FaArrowLeft, FaCheck, FaCog, FaRegClock, FaTimes } from 'react-icons/fa';
import { useLocale } from '../context/LocaleContext';
import ServiceIcon from './ServiceIcon';
import ServiceOrderModal from './ServiceOrderModal';
import './PricingPlans.css';

const INITIAL_FEATURES_COUNT = 6;
const isRequestGroup = (group) => group?.mode === 'request';

const DynamicPricingPlans = ({ service }) => {
  const { t } = useTranslation();
  const { formatNumber, locale } = useLocale();
  const groups = useMemo(
    () => service.planGroups.filter((group) => isRequestGroup(group) || group.plans?.length),
    [service.planGroups]
  );
  const isWebsiteSupport = service?.slug === 'website-support' || Number(service?.legacyId) === 5;
  const displayGroups = useMemo(() => (
    isWebsiteSupport
      ? [...groups].sort((a, b) => Number(isRequestGroup(b)) - Number(isRequestGroup(a)))
      : groups
  ), [groups, isWebsiteSupport]);
  const [activeGroup, setActiveGroup] = useState(groups[0]?.id || '');
  const [expandedPlans, setExpandedPlans] = useState({});
  const [selection, setSelection] = useState(null);
  const sectionRef = useRef(null);

  useEffect(() => {
    if (!groups.some((group) => String(group.id) === String(activeGroup))) {
      setActiveGroup(groups[0]?.id || '');
    }
  }, [activeGroup, groups]);

  useEffect(() => {
    let pending;
    try { pending = JSON.parse(sessionStorage.getItem('pendingServicePlan') || 'null'); } catch (_) { pending = null; }
    if (!pending || ![service._id, service.id].some((id) => id && String(id) === String(pending.serviceId))) return;
    const pendingGroup = groups.find((item) => String(item.id) === String(pending.groupId));
    if (!pendingGroup) return;
    if (pending.selectionType === 'quote_request' && isRequestGroup(pendingGroup)) {
      setActiveGroup(pendingGroup.id);
      setSelection({ group: pendingGroup, plan: null });
      return;
    }
    const pendingPlan = pendingGroup.plans?.find((item) => String(item.id) === String(pending.planId));
    if (pendingPlan) {
      setActiveGroup(pendingGroup.id);
      setSelection({ group: pendingGroup, plan: pendingPlan });
    }
  }, [groups, service._id, service.id]);

  const group = groups.find((item) => String(item.id) === String(activeGroup)) || groups[0];
  if (!group) return null;
  const activeTabIndex = Math.max(0, groups.findIndex((item) => String(item.id) === String(group.id)));
  const closeModal = () => {
    sessionStorage.removeItem('pendingServicePlan');
    setSelection(null);
  };

  const renderGroupContent = (currentGroup) => {
    if (isRequestGroup(currentGroup)) {
      return (
        <section className="pricing-quote-request">
          <div className="pricing-quote-request-copy">
            <span className="pricing-quote-request-badge">
              <FaRegClock /> {currentGroup.request?.badge || t('serviceUi.pricing.noPaymentBadge')}
            </span>
            <h4>{currentGroup.request?.title || currentGroup.title}</h4>
            <p>{currentGroup.request?.description}</p>
            <button type="button" onClick={() => setSelection({ group: currentGroup, plan: null })}>
              {currentGroup.request?.buttonText || t('serviceUi.pricing.submitRequest')}
              <FaArrowLeft />
            </button>
          </div>
          <div className="pricing-quote-request-flow" aria-label={t('serviceUi.pricing.requestFlowLabel')}>
            {(currentGroup.request?.highlights || []).map((highlight, index) => (
              <div key={highlight}>
                <span>{formatNumber(index + 1)}</span>
                <p>{highlight}</p>
              </div>
            ))}
          </div>
        </section>
      );
    }

    return (
      <div className="pricing-plans-grid">
        {currentGroup.plans.map((plan) => {
          const planKey = `${currentGroup.id}-${plan.id}`;
          const expanded = Boolean(expandedPlans[planKey]);
          return (
            <div key={planKey} className={`pricing-plan-card ${plan.popular ? 'popular' : ''}`}>
              {plan.popular && <div className="pricing-plan-badge">{t('serviceUi.pricing.popular')}</div>}
              <div className="pricing-plan-header">
                <h4 className="pricing-plan-name">{plan.name}</h4>
                <div className="pricing-plan-price">
                  <span className="pricing-plan-amount">
                    {plan.priceAvailable === false
                      ? t('serviceUi.order.review.priceAfterReview')
                      : formatNumber(plan.price, { maximumFractionDigits: 2 })}
                  </span>
                  {plan.priceAvailable !== false && <span className="pricing-plan-currency">{plan.currency}</span>}
                </div>
                <p className="pricing-plan-period">{plan.period}</p>
              </div>
              <div className="pricing-plan-features">
                {plan.features.map((feature, index) => (expanded || index < INITIAL_FEATURES_COUNT) && (
                  <div key={`${planKey}-${index}`} className={`pricing-plan-feature ${feature.included ? 'included' : 'excluded'}`}>
                    {feature.included
                      ? <FaCheck className="pricing-plan-feature-icon included-icon" />
                      : <FaTimes className="pricing-plan-feature-icon excluded-icon" />}
                    <span className="pricing-plan-feature-text">{feature.text}</span>
                  </div>
                ))}
              </div>
              {plan.features.length > INITIAL_FEATURES_COUNT && (
                <button
                  type="button"
                  className="pricing-plan-more-btn"
                  onClick={() => setExpandedPlans((current) => ({ ...current, [planKey]: !current[planKey] }))}
                >
                  {expanded ? t('serviceUi.common.showLess') : t('serviceUi.common.showMore')}
                </button>
              )}
              <button type="button" className="pricing-plan-button" onClick={() => setSelection({ group: currentGroup, plan })}>
                {plan.buttonText || t('serviceUi.pricing.selectPlan')}
              </button>
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <div id="pricing-plans" className="pricing-plans-section" ref={sectionRef}>
      <div className="service-details-section">
        <h3 className="service-details-section-title">{service.pricingTitle || t('serviceUi.pricing.title')}</h3>
      </div>

      {isWebsiteSupport ? (
        <div className="pricing-stacked-groups">
          {displayGroups.map((item) => (
            <section
              key={item.id}
              className={`pricing-stacked-group ${isRequestGroup(item) ? 'pricing-stacked-group-request' : ''}`.trim()}
            >
              <div className="pricing-stacked-group-heading">
                <ServiceIcon name={item.icon} className="pricing-stacked-group-icon" />
                <h4>{item.title}</h4>
              </div>
              {renderGroupContent(item)}
            </section>
          ))}
        </div>
      ) : (
        <>
          {groups.length > 1 && (
            <div className="pricing-tabs-shell">
              <p className="pricing-tabs-hint" id="pricing-tabs-hint">
                {t('serviceUi.pricing.tabsHint')}
              </p>
              <div
                className={`pricing-tabs ${isRequestGroup(group) ? 'is-request' : ''}`.trim()}
                role="tablist"
                aria-labelledby="pricing-tabs-hint"
                aria-label={t('serviceUi.pricing.tabsLabel')}
                style={{
                  '--pricing-tab-count': groups.length,
                  '--pricing-tab-index': activeTabIndex,
                  '--pricing-tab-dir': locale?.direction === 'ltr' ? 1 : -1,
                }}
              >
                <span className="pricing-tabs-indicator" aria-hidden="true" />
                {groups.map((item, index) => {
                  const isActive = String(group.id) === String(item.id);
                  return (
                    <button
                      type="button"
                      key={item.id}
                      id={`pricing-tab-${item.id}`}
                      role="tab"
                      aria-selected={isActive}
                      aria-controls="pricing-tab-panel"
                      tabIndex={isActive ? 0 : -1}
                      className={`pricing-tab ${isRequestGroup(item) ? 'pricing-tab-request' : ''} ${isActive ? 'active' : ''}`.trim()}
                      onClick={() => setActiveGroup(item.id)}
                      onKeyDown={(event) => {
                        if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
                        event.preventDefault();
                        const last = groups.length - 1;
                        const rtl = getComputedStyle(event.currentTarget).direction === 'rtl';
                        let nextIndex = index;
                        if (event.key === 'Home') nextIndex = 0;
                        else if (event.key === 'End') nextIndex = last;
                        else {
                          const moveNext = event.key === 'ArrowRight' ? !rtl : rtl;
                          nextIndex = (index + (moveNext ? 1 : -1) + groups.length) % groups.length;
                        }
                        setActiveGroup(groups[nextIndex].id);
                        requestAnimationFrame(() => {
                          document.getElementById(`pricing-tab-${groups[nextIndex].id}`)?.focus();
                        });
                      }}
                    >
                      <span className="pricing-tab-icon" aria-hidden="true">
                        <ServiceIcon name={item.icon} />
                      </span>
                      <span className="pricing-tab-label">{item.title}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          )}
          <div
            key={group.id}
            id="pricing-tab-panel"
            className="pricing-tab-panel"
            role={groups.length > 1 ? 'tabpanel' : undefined}
            aria-labelledby={groups.length > 1 && group.id ? `pricing-tab-${group.id}` : undefined}
          >
            {renderGroupContent(group)}
          </div>
        </>
      )}

      {(service.pricingNoteTitle || service.pricingNoteText) && (
        <div className="pricing-plans-note">
          <div className="pricing-plans-note-icon-wrapper"><FaCog className="pricing-plans-note-icon" /></div>
          <h4 className="pricing-plans-note-title">{service.pricingNoteTitle}</h4>
          <div className="pricing-plans-note-content">
            <div className="pricing-plans-note-item"><p className="pricing-plans-note-text">{service.pricingNoteText}</p></div>
          </div>
        </div>
      )}

      {selection && (
        <ServiceOrderModal
          service={service}
          group={selection.group}
          plan={selection.plan}
          onClose={closeModal}
        />
      )}
    </div>
  );
};

export default DynamicPricingPlans;
